import * as _React from 'react'
import {useCallback, useEffect, useRef, useState} from 'react'
import {ActivityIndicator, StyleSheet, Text, View} from 'react-native'

import {useProfileQuery} from '#/state/queries/profile'
import {useSession} from '#/state/session'
// @ts-ignore - JS module, no types
import {loadJitsiExternalApi} from '../../../../web/jitsi-loader'
import {logRecordingAttempt} from '../api/logRecordingAttempt'
import {useScreenRecordingDetection} from '../hooks/useScreenRecordingDetection'

// Import the avatar URL processing function used by UserAvatar component
// HACK function from UserAvatar to ensure consistent avatar URL handling
function hackModifyThumbnailPath(uri: string, isEnabled: boolean): string {
  return isEnabled
    ? uri.replace('/img/avatar/plain/', '/img/avatar_thumbnail/plain/')
    : uri
}

function stripBskySuffix(handle: string): string {
  return handle.replace(/\.bsky\.social$/i, '')
}

// Type definition for the JitsiMeetExternalAPI
declare global {
  interface Window {
    JitsiMeetExternalAPI?: any
  }
}

interface JitsiMeetingProps {
  roomId: string
  roomName: string
  roomTags?: string[]
  onClose: () => void
  onRecordingDetected: () => void
}

export function JitsiMeeting({
  roomId,
  roomName,
  roomTags = [],
  onClose,
  onRecordingDetected,
}: JitsiMeetingProps) {
  const {currentAccount} = useSession()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const jitsiApiRef = useRef<any>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)

  // Store callbacks in refs so they don't trigger re-initialization
  const onCloseRef = useRef(onClose)
  const onRecordingDetectedRef = useRef(onRecordingDetected)
  const roomNameRef = useRef(roomName)

  // Update refs when props change, but don't trigger re-initialization
  useEffect(() => {
    onCloseRef.current = onClose
    onRecordingDetectedRef.current = onRecordingDetected
    roomNameRef.current = roomName
  }, [onClose, onRecordingDetected, roomName])

  // Get the current user's profile to access their avatar
  const {data: profile} = useProfileQuery({did: currentAccount?.did!})

  const profileAvatarRef = useRef<string | undefined>(profile?.avatar)
  useEffect(() => {
    profileAvatarRef.current = profile?.avatar
  }, [profile?.avatar])

  // Check if room has Recording flair
  const hasRecordingFlair = roomTags.includes('Recording')

  // Store hasRecordingFlair in ref to prevent recreating handleRecordingDetected
  const hasRecordingFlairRef = useRef(hasRecordingFlair)
  useEffect(() => {
    hasRecordingFlairRef.current = hasRecordingFlair
  }, [hasRecordingFlair])

  // Handle recording detection with FULLY stable dependencies (never changes)
  const handleRecordingDetected = useCallback(
    async (detectionMethod: string) => {
      console.log(
        `⚠️ Recording detected via ${detectionMethod} in room without Recording flair`,
      )

      // Log the attempt to backend using refs for all values
      await logRecordingAttempt({
        roomId,
        roomName: roomNameRef.current,
        userDid: currentAccountRef.current?.did || 'unknown',
        userHandle: currentAccountRef.current?.handle || 'unknown',
        detectionMethod,
        roomHadRecordingFlair: hasRecordingFlairRef.current,
      })

      // Trigger the kick callback using the ref
      onRecordingDetectedRef.current()
    },
    [roomId], // Only roomId, which should never change during a meeting
  )

  // Initialize screen recording detection
  useScreenRecordingDetection({
    roomId,
    roomName,
    hasRecordingFlair,
    userDid: currentAccount?.did || '',
    userHandle: currentAccount?.handle || '',
    onRecordingDetected: handleRecordingDetected,
    enabled: !!currentAccount?.did, // Only enable if user is logged in
  })

  // Generate a Jitsi room identifier using the user-defined room name
  const generateRoomName = useCallback(() => {
    // P2P rooms: use the roomId directly as the Jitsi room name
    // The p2p- prefix tells Prosody to enforce 2-participant limit
    if (roomId.startsWith('p2p-')) {
      return roomId
    }

    // Start with a base name derived from the user-defined room name
    let baseName = ''
    let displayName = ''

    if (roomId.startsWith('join-')) {
      const lastDashIndex = roomId.lastIndexOf('-')
      if (lastDashIndex > 5) {
        // 'join-' is 5 characters
        // Extract the user-defined room name part (between 'join-' and the last dash)
        const roomNamePart = roomId.substring(5, lastDashIndex)

        // Get the uniqueId part for ensuring room uniqueness
        const uniqueId = roomId.substring(lastDashIndex + 1)

        // Use the room name part as the base name, but append the uniqueId in a
        // way that won't affect the display (with an underscore separator)
        baseName = `${roomNamePart}_${uniqueId}`

        // Set the display name to be shown in the UI
        displayName = roomNamePart
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')

        // Log what we're using for debugging
        console.log(
          'Using room name:',
          displayName,
          'with base identifier:',
          baseName,
        )
      }
    }

    if (!baseName) {
      // Fallback: Sanitize the room name to make it compatible with Jitsi
      baseName = roomId
        .replace(/[^\w\s-]/g, '') // Remove special chars except underscore, hyphen, and space
        .replace(/\s+/g, '_') // Replace spaces with underscores
        .toLowerCase() // Convert to lowercase

      if (!baseName) {
        baseName = 'bsky'
      }
    }

    return baseName
  }, [roomId])

  // Store current account in ref to avoid recreating tracking functions
  const currentAccountRef = useRef(currentAccount)
  useEffect(() => {
    currentAccountRef.current = currentAccount
  }, [currentAccount])

  // Initialize Jitsi Meet API
  useEffect(() => {
    if (typeof window === 'undefined') return

    // Wait for container to be available
    if (!containerRef.current) return

    // Prevent re-initialization if API is already initialized
    if (jitsiApiRef.current) {
      console.log('Jitsi API already initialized, skipping re-initialization')
      return
    }

    const userDisplayName = currentAccount?.handle
      ? stripBskySuffix(currentAccount.handle)
      : 'Guest'

    // Prepare avatar URL
    let userAvatarUrl: string | undefined
    if (profile?.avatar) {
      userAvatarUrl = hackModifyThumbnailPath(profile.avatar, false)
      console.log(
        'Setting avatar URL for Jitsi (using profile avatar):',
        userAvatarUrl,
      )
    } else if (currentAccount?.handle) {
      const encodedUsername = encodeURIComponent(currentAccount.handle)
      userAvatarUrl = `https://ui-avatars.com/api/?name=${encodedUsername}&background=random&size=200`
      console.log('Setting fallback avatar URL for Jitsi:', userAvatarUrl)
    }

    const initJitsi = async () => {
      // Fetch a room-specific JWT token from the backend before initializing.
      // The worker decides which Jitsi backend (8x8 JaaS or self-hosted VPS)
      // to use and returns the matching domain + final room name.
      const tokenRes = await fetch('/api/jitsi/token', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
          roomSlug: roomId,
          userDid: currentAccount?.did,
          userHandle: currentAccount?.handle,
          avatarUrl: userAvatarUrl,
        }),
      })

      if (!tokenRes.ok) {
        const errBody = await tokenRes.json().catch(() => null)
        if (errBody?.code === 'ROOM_FULL') {
          setError('This 1:1 room is currently full (2 participants).')
          setLoading(false)
          return
        }
        console.error('Failed to get Jitsi token:', errBody)
        setError('Failed to connect to room.')
        setLoading(false)
        return
      }

      const {
        token,
        domain,
        roomName: tokenRoomName,
        scriptUrl,
      } = await tokenRes.json()
      const jitsiRoomName = tokenRoomName || generateRoomName()

      try {
        await loadJitsiExternalApi(
          scriptUrl || `https://${domain}/external_api.js`,
        )
        if (!window.JitsiMeetExternalAPI) {
          console.error('Jitsi Meet External API failed to load')
          return
        }
        if (!containerRef.current) return
        // Safari requires the `allow` attribute on the iframe BEFORE it loads,
        // otherwise getUserMedia() is permanently blocked in the cross-origin
        // frame. Monkey-patch createElement so the Jitsi API's iframe gets the
        // attribute at creation time, not after.
        const origCreateElement = document.createElement.bind(document)
        document.createElement = function (
          tagName: string,
          options?: ElementCreationOptions,
        ) {
          const el = origCreateElement(tagName, options)
          if (tagName.toLowerCase() === 'iframe') {
            ;(el as HTMLIFrameElement).allow =
              'camera; microphone; display-capture; autoplay; clipboard-write; speaker-selection'
          }
          return el
        } as typeof document.createElement

        // Create the Jitsi Meet External API instance
        const options = {
          roomName: jitsiRoomName,
          jwt: token,
          width: '100%',
          height: '100%',
          parentNode: containerRef.current,

          // User info
          userInfo: {
            displayName: userDisplayName,
            ...(userAvatarUrl && {avatarURL: userAvatarUrl}),
          },

          // Simplifying config to essentials only
          configOverwrite: {
            // Disable prejoin page - use modern config (prejoinPageEnabled is deprecated)
            prejoinConfig: {
              enabled: false,
              hideDisplayName: true,
              hideExtraJoinButtons: ['no-audio', 'by-phone'],
            },

            // Keep deprecated setting as fallback for older Jitsi versions
            prejoinPageEnabled: false,

            // Prevent "open in app" prompt on mobile browsers
            disableDeepLinking: true,

            // Skip hangup confirmation dialog - just leave immediately
            hangupMenuEnabled: false,
            enableEndConference: false,
            disableEndConference: true,

            // Audio/video settings
            startWithAudioMuted: true,
            startWithVideoMuted: true,

            // Disable all notification sounds to prevent setSinkId errors in iframe
            disabledSounds: [
              'ASKED_TO_UNMUTE_SOUND',
              'E2EE_OFF_SOUND',
              'E2EE_ON_SOUND',
              'INCOMING_MSG_SOUND',
              'KNOCKING_PARTICIPANT_SOUND',
              'LIVE_STREAMING_OFF_SOUND',
              'LIVE_STREAMING_ON_SOUND',
              'NO_AUDIO_SIGNAL_SOUND',
              'NOISY_AUDIO_INPUT_SOUND',
              'OUTGOING_CALL_EXPIRED_SOUND',
              'OUTGOING_CALL_REJECTED_SOUND',
              'OUTGOING_CALL_RINGING_SOUND',
              'OUTGOING_CALL_START_SOUND',
              'PARTICIPANT_JOINED_SOUND',
              'PARTICIPANT_LEFT_SOUND',
              'RAISE_HAND_SOUND',
              'REACTION_SOUND',
              'RECORDING_OFF_SOUND',
              'RECORDING_ON_SOUND',
              'TALK_WHILE_MUTED_SOUND',
            ],

            // Set the room display name
            subject: roomNameRef.current,

            // Interface simplification - removed 'desktop' to disable screen sharing.
            // 1o1 rooms drop '__copyRoomUrl' since the room URL is single-use
            // and shared out-of-band. Group rooms keep it for invites.
            toolbarButtons: roomId.startsWith('p2p-personal-')
              ? ['microphone', 'camera', 'chat', 'tileview', '__hangup']
              : [
                  'microphone',
                  'camera',
                  'chat',
                  'tileview',
                  '__copyRoomUrl',
                  '__hangup',
                ],

            // 1:1 rooms: hide the right-side filmstrip and the per-tile "…" menu.
            // Note: the config key is `filmstrip` (lowercase 's'), not filmStrip.
            ...(roomId.startsWith('p2p-personal-') && {
              filmstrip: {disabled: true},
              disable1On1Mode: true,
              remoteVideoMenu: {
                disabled: true,
                disableKick: true,
                disableGrantModerator: true,
                disablePrivateChat: true,
              },
              disableLocalVideoFlip: true,
              disableSelfViewSettings: true,
              // Suppress the "X is asking to join" toasts. The lobby is auto-
              // managed by knockingParticipant — the owner doesn't need a UI
              // for it.
              disabledNotifications: [
                'notify.participantWantsToJoin',
                'notify.participantsWantToJoin',
              ],
            }),

            // Multiple configuration options to fully hide polls
            polls: {
              enabled: false,
            },

            // Hide polls from the interface
            hiddenPremeetingButtons: ['polls'],
            disablePolls: true,

            // Configure the chat UI to only show chat features
            chatButtons: ['chat'],

            // Additional interface configuration
            buttonsWithNotifyClick: ['__copyRoomUrl', '__hangup'],

            // Custom toolbar buttons configuration
            customToolbarButtons: [
              {
                icon: 'https://img.icons8.com/material-outlined/96/ffffff/link--v1.png',
                id: '__copyRoomUrl',
                text: 'Copy Room Link',
              },
              {
                icon: 'https://img.icons8.com/material/96/ffffff/end-call.png',
                id: '__hangup',
                text: 'Leave Room',
                backgroundColor: '#e53935',
              },
            ],
          },

          interfaceConfigOverwrite: {
            DEFAULT_BACKGROUND: '#000000',
            SETTINGS_SECTIONS: ['language', 'profile', 'sounds'],
          },
        }

        // Initialize Jitsi Meet API
        const api = new window.JitsiMeetExternalAPI(domain, options)

        // Restore original createElement now that the iframe has been created
        document.createElement = origCreateElement

        jitsiApiRef.current = api

        // Add custom toolbar button for copying room URL
        api.addListener('toolbarButtonClicked', (event: any) => {
          if (event.key === '__copyRoomUrl') {
            // Generate the room URL
            let roomUrl: string
            if (roomId.startsWith('p2p-personal-')) {
              // Personal 1:1 rooms use /1o1/r/{token} where token is the
              // random suffix of the slug.
              const tokenPart = roomId.replace('p2p-personal-', '')
              roomUrl = `${window.location.origin}/1o1/r/${tokenPart}`
            } else if (roomId.startsWith('p2p-')) {
              roomUrl = `${window.location.origin}/${roomId}`
            } else {
              roomUrl = `${window.location.origin}/rooms/${roomId}`
            }

            // Copy to clipboard
            navigator.clipboard
              .writeText(roomUrl)
              .then(() => {
                console.log('✅ Room URL copied to clipboard:', roomUrl)
                // Show a notification to the user
                api.executeCommand('displayNotification', {
                  title: 'Link Copied!',
                  description: 'Room URL copied to clipboard',
                  type: 'success',
                })
              })
              .catch(err => {
                console.error('Failed to copy room URL:', err)
                api.executeCommand('displayNotification', {
                  title: 'Copy Failed',
                  description: 'Could not copy room URL',
                  type: 'error',
                })
              })
          } else if (event.key === '__hangup') {
            api.executeCommand('hangup')
          }
        })

        // Set up event listeners
        api.addEventListeners({
          videoConferenceJoined: async () => {
            // Hide loading overlay and cancel timeout
            setLoading(false)
            clearTimeout((api as any)._loadingTimeout)
            console.log('User has joined the room')

            // Track participant join in database
            const account = currentAccountRef.current
            if (account?.handle && account?.did) {
              try {
                const response = await fetch('/api/participants/join', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    room_slug: roomId,
                    username: account.handle,
                    user_did: account.did,
                  }),
                })

                if (!response.ok) {
                  console.error(
                    'Failed to track participant join:',
                    await response.text(),
                  )
                } else {
                  console.log('✅ Participant join tracked successfully')
                }
              } catch (error) {
                console.error('Error tracking participant join:', error)
              }
            }

            // Set display name (without bsky: prefix)
            api.executeCommand('displayName', userDisplayName)

            // Set avatar URL for when camera is off
            // Use profile avatar ref (latest value) if loaded by now, otherwise use the fallback
            const currentAvatarUrl = profileAvatarRef.current
              ? hackModifyThumbnailPath(profileAvatarRef.current, false)
              : userAvatarUrl
            if (currentAvatarUrl) {
              console.log('Setting avatar URL in join event:', currentAvatarUrl)
              api.executeCommand('avatarUrl', currentAvatarUrl)
            }

            // Double-ensure the room subject is set correctly using the room name ref
            // This reinforces the initial config setting
            console.log('Setting room subject to:', roomNameRef.current)
            api.executeCommand('subject', roomNameRef.current)

            // 1:1 rooms: collapse the filmstrip at runtime. JaaS often ignores
            // the configOverwrite.filmStrip flag, but the toggle command works.
            if (roomId.startsWith('p2p-personal-')) {
              try {
                api.executeCommand('toggleFilmStrip')
              } catch (e) {
                console.warn('toggleFilmStrip failed:', e)
              }

              // Enable lobby so a 3rd person can never see the room. The
              // knockingParticipant handler below auto-admits up to one guest
              // and rejects the rest.
              try {
                api.executeCommand('toggleLobby', true)
              } catch (e) {
                console.warn('toggleLobby failed:', e)
              }
            }

            // Note: prejoinConfig must be set in initial options, not here (too late)
          },
          readyToClose: async () => {
            // Track participant leave before closing
            const account = currentAccountRef.current
            if (account?.handle && account?.did) {
              try {
                await fetch('/api/participants/leave', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    room_slug: roomId,
                    username: account.handle,
                    user_did: account.did,
                  }),
                })
                console.log('✅ Participant leave tracked successfully')
              } catch (error) {
                console.error('Error tracking participant leave:', error)
              }
            }
            onCloseRef.current()
          },
          videoConferenceLeft: async () => {
            // Track participant leave before closing
            const account = currentAccountRef.current
            if (account?.handle && account?.did) {
              try {
                await fetch('/api/participants/leave', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    room_slug: roomId,
                    username: account.handle,
                    user_did: account.did,
                  }),
                })
                console.log('✅ Participant leave tracked successfully')
              } catch (error) {
                console.error('Error tracking participant leave:', error)
              }
            }
            onCloseRef.current()
          },
          participantLeft: (participant: any) => {
            console.log('Participant left', participant)
            // Note: This tracks OTHER participants leaving, not the current user
          },
          participantJoined: (participant: any) => {
            console.log('Participant joined', participant)
            // 1:1 cap enforcement: owner kicks anyone past the second seat.
            // Counts include the local user, so >= 2 OTHER participants means
            // the room already has 3+ people total.
            if (roomId.startsWith('p2p-personal-')) {
              try {
                const total = api.getNumberOfParticipants?.() ?? 0
                if (total > 2 && participant?.id) {
                  console.log(
                    `[1o1-cap] kicking participant ${participant.id} (total=${total})`,
                  )
                  api.executeCommand('kickParticipant', participant.id)
                }
              } catch (e) {
                console.error('1o1 cap enforcement failed:', e)
              }
            }
          },
          audioMuteStatusChanged: (muted: boolean) => {
            console.log('Audio mute status changed', muted)
          },
          videoMuteStatusChanged: (muted: boolean) => {
            console.log('Video mute status changed', muted)
          },
        })

        // Listen for conference failures
        api.addListener('videoConferenceFailed', (event: any) => {
          console.error('Video conference failed:', event)
          setLoading(false)
          setError('Failed to connect to room. Please try again.')
        })

        // 1:1 lobby gate: admit the first knocker, reject any further ones.
        // This stops a 3rd person from ever seeing the room contents — they
        // sit in the lobby and get rejected before any media flows.
        if (roomId.startsWith('p2p-personal-')) {
          api.addListener('knockingParticipant', (event: any) => {
            const knocker = event?.participant ?? event
            const knockerId = knocker?.id
            if (!knockerId) return
            try {
              const total = api.getNumberOfParticipants?.() ?? 0
              // total counts the local user + anyone already admitted.
              // < 2 means the seat is open; >= 2 means the room is already full.
              const admit = total < 2
              console.log(
                `[1o1-lobby] knocker=${knockerId} total=${total} -> ${
                  admit ? 'admit' : 'reject'
                }`,
              )
              api.executeCommand('answerKnockingParticipant', knockerId, admit)
            } catch (e) {
              console.error('answerKnockingParticipant failed:', e)
            }
          })
        }

        // Timeout if room never loads (cleared when videoConferenceJoined fires)
        const loadingTimeout = setTimeout(() => {
          console.error('Jitsi room loading timed out after 30s')
          setLoading(false)
          setError('Room took too long to load. Please try again.')
        }, 30000)

        // Store timeout for cleanup
        ;(api as any)._loadingTimeout = loadingTimeout
      } catch (error) {
        console.error('Error initializing Jitsi Meet API:', error)
        setError('Failed to initialize video room.')
        setLoading(false)
      }
    } // end initJitsi

    initJitsi()

    return () => {
      if (jitsiApiRef.current) {
        clearTimeout((jitsiApiRef.current as any)._loadingTimeout)
        jitsiApiRef.current.dispose()
        jitsiApiRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // Only re-initialize if these critical values change
    currentAccount?.handle,
    currentAccount?.did,
    roomId, // Room ID should never change for a given meeting instance
  ])

  // When profile avatar loads after Jitsi is already initialized, push it to Jitsi
  useEffect(() => {
    if (profile?.avatar && jitsiApiRef.current) {
      const avatarUrl = hackModifyThumbnailPath(profile.avatar, false)
      console.log('Profile avatar loaded, updating Jitsi avatar:', avatarUrl)
      jitsiApiRef.current.executeCommand('avatarUrl', avatarUrl)
    }
  }, [profile?.avatar])

  return (
    <View style={styles.container}>
      <div
        ref={containerRef}
        className="jitsi-container"
        style={{
          width: '100%',
          height: '100%',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: '#000',
          zIndex: 9999,
        }}
      />
      {loading && !error && (
        <View style={styles.loadingOverlay}>
          <ActivityIndicator size="large" color="#fd7c21" />
          <Text style={styles.loadingText}>Loading Room</Text>
        </View>
      )}
      {error && (
        <View style={styles.loadingOverlay}>
          <Text style={styles.errorText}>{error}</Text>
        </View>
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    height: '100%',
    width: '100%',
    backgroundColor: '#000',
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#000',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10000,
  },
  loadingText: {
    color: '#fff',
    marginTop: 12,
    fontSize: 16,
  },
  errorText: {
    color: '#ff6b6b',
    fontSize: 16,
    textAlign: 'center',
    paddingHorizontal: 20,
  },
})
